import {AfterViewInit, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CloverPayServiceService} from '../../../_services/clover-pay-service.service';
import {environment} from '../../../../environments/environment';

declare const Clover: any;

export interface ManualCardEntryDialogData {
  orderId: string;
  amount: number;          // cents — order total (no tip)
  businessName?: string;
  customerEmail?: string;
}

export interface ManualCardEntryDialogResult {
  success: boolean;
  paymentResponse?: any;
  error?: string;
}

/**
 * Feature 3 — Manual card entry via the Clover hosted iframe SDK
 * (SAQ-A path). The PAN/exp/CVV are entered into Clover-served iframes
 * so the cashier device never touches raw card data; the SDK returns a
 * one-shot token which is exchanged for a charge via
 * POST /cloverPay/processIframePayment.
 *
 * Constraints baked into this dialog by product:
 *  - NO tip prompt (cashier-register manual card flow is for ad-hoc
 *    charges; tip is always 0).
 *  - On success, the dialog closes with {success:true, paymentResponse}
 *    and the caller is responsible for routing through
 *    onPaymentCompleted() so the receipt popup shows.
 *  - Card data never leaves Clover's iframe. We do NOT bind to the
 *    input value; we only call `clover.createToken()`.
 */
@Component({
  selector: 'app-manual-card-entry-dialog',
  templateUrl: './manual-card-entry-dialog.component.html',
  styleUrls: ['./manual-card-entry-dialog.component.scss'],
})
export class ManualCardEntryDialogComponent implements AfterViewInit, OnDestroy {

  loadingSdk = true;
  sdkError: string | null = null;
  submitting = false;
  errorMessage: string | null = null;
  formReady = false;

  private clover: any;
  private elements: any;
  private cardNumber: any;
  private cardDate: any;
  private cardCvv: any;
  private cardPostalCode: any;
  private injectedScript: HTMLScriptElement | null = null;

  constructor(
    public dialogRef: MatDialogRef<ManualCardEntryDialogComponent, ManualCardEntryDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ManualCardEntryDialogData,
    private cloverPayService: CloverPayServiceService,
    private cdRef: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this._ensureSdkLoaded()
      .then(() => this._initCloverIframe())
      .catch(err => this.zone.run(() => {
        this.loadingSdk = false;
        this.sdkError = err?.message || 'Failed to load Clover SDK';
        this.cdRef.detectChanges();
      }));
  }

  ngOnDestroy(): void {
    try { this.cardNumber?.unmount?.(); } catch (_) {}
    try { this.cardDate?.unmount?.(); } catch (_) {}
    try { this.cardCvv?.unmount?.(); } catch (_) {}
    try { this.cardPostalCode?.unmount?.(); } catch (_) {}
  }

  formatCents(cents: number): string {
    return '$' + ((cents || 0) / 100).toFixed(2);
  }

  cancel(): void {
    if (this.submitting) { return; }
    this.dialogRef.close(undefined);
  }

  submit(): void {
    if (this.submitting || !this.formReady) { return; }
    this.submitting = true;
    this.errorMessage = null;
    this.clover.createToken().then((result: any) => {
      this.zone.run(() => {
        if (result && result.errors) {
          // Clover returns a hash of per-field errors; surface the first
          // non-empty one for the cashier so they can correct the entry.
          const firstError = Object.values(result.errors).find(v => !!v) as string | undefined;
          this.errorMessage = firstError || 'Please correct the highlighted fields and try again.';
          this.submitting = false;
          this.cdRef.detectChanges();
          return;
        }
        if (!result?.token) {
          this.errorMessage = 'Card tokenization failed. Please try again.';
          this.submitting = false;
          this.cdRef.detectChanges();
          return;
        }
        this._chargeWithToken(result.token);
      });
    }).catch((err: any) => {
      this.zone.run(() => {
        this.errorMessage = err?.message || 'Could not tokenize card.';
        this.submitting = false;
        this.cdRef.detectChanges();
      });
    });
  }

  private _chargeWithToken(token: string): void {
    const payload: any = {
      token,
      orderId: this.data.orderId,
      amount: this.data.amount,
      tipAmount: 0,          // Feature 3: skip tip
      currency: 'USD',
    };
    if (this.data.customerEmail) { payload.email = this.data.customerEmail; }

    this.cloverPayService.processIframePayment(payload).subscribe(
      (resp: any) => {
        this.dialogRef.close({success: true, paymentResponse: resp});
      },
      (err: any) => {
        this.errorMessage = (err && (err.error?.message || err.message)) || 'Payment failed. Please try again.';
        this.submitting = false;
        this.cdRef.detectChanges();
      },
    );
  }

  private _ensureSdkLoaded(): Promise<void> {
    if (typeof Clover !== 'undefined') { return Promise.resolve(); }
    return new Promise((resolve, reject) => {
      const src = environment.production
        ? 'https://checkout.clover.com/sdk.js'
        : 'https://checkout.sandbox.dev.clover.com/sdk.js';
      // If a script tag already exists (other components loaded it), wait
      // for `Clover` to appear rather than re-injecting.
      const existing = Array.from(document.scripts).find(s => s.src === src);
      if (existing) {
        const start = Date.now();
        const wait = () => {
          if (typeof Clover !== 'undefined') { resolve(); return; }
          if (Date.now() - start > 8000) { reject(new Error('Clover SDK load timed out')); return; }
          setTimeout(wait, 100);
        };
        wait();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to fetch Clover SDK'));
      document.body.appendChild(script);
      this.injectedScript = script;
    });
  }

  private async _initCloverIframe(): Promise<void> {
    const keyResp: any = await this.cloverPayService.getAPIKey().toPromise();
    const apiAccessKey = keyResp?.pakms?.apiAccessKey;
    const merchantId = keyResp?.merchantId;
    if (!apiAccessKey || !merchantId) {
      throw new Error('Clover credentials unavailable');
    }
    this.clover = new Clover(apiAccessKey, {merchantId});
    this.elements = this.clover.elements();
    const styles = {
      body:  {fontFamily: 'Roboto, Open Sans, sans-serif', fontSize: '16px'},
      input: {fontSize: '16px'},
    };
    this.cardNumber     = this.elements.create('CARD_NUMBER', styles);
    this.cardDate       = this.elements.create('CARD_DATE', styles);
    this.cardCvv        = this.elements.create('CARD_CVV', styles);
    this.cardPostalCode = this.elements.create('CARD_POSTAL_CODE', styles);
    // The mount targets exist in the dialog template — give Angular one
    // tick to render them before mounting.
    setTimeout(() => {
      try {
        this.cardNumber.mount('#mce-card-number');
        this.cardDate.mount('#mce-card-date');
        this.cardCvv.mount('#mce-card-cvv');
        this.cardPostalCode.mount('#mce-card-postal');
        this.loadingSdk = false;
        this.formReady = true;
        this.cdRef.detectChanges();
      } catch (e: any) {
        this.loadingSdk = false;
        this.sdkError = e?.message || 'Could not display card entry fields.';
        this.cdRef.detectChanges();
      }
    }, 0);
  }
}
